import React from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import {
  CalendarClock, PackageOpen, Wallet, Undo2, Clock, FileClock,
  ReceiptText, TrendingUp, Wrench, AlertTriangle,
} from "lucide-react";

const COLORS = ["#16a34a", "#525252", "#a3a3a3", "#22c55e", "#065f46"];

function StatCard({ icon: Icon, label, value, onClick, accent = "green", testId }) {
  return (
    <button
      onClick={onClick}
      className="text-left card-flat p-4 hover:border-green-600 transition-colors duration-150 shadow-none"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <Icon className={`h-4 w-4 ${accent === "green" ? "text-green-600" : "text-neutral-500"}`} />
      </div>
      <div className="text-2xl font-bold mt-2 tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>{value}</div>
      <div className="text-xs text-neutral-500 mt-0.5">{label}</div>
    </button>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = React.useState(null);
  const [charts, setCharts] = React.useState(null);
  const nav = useNavigate();

  React.useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([api.get("/dashboard/stats"), api.get("/dashboard/charts")]);
        setStats(s.data);
        setCharts(c.data);
      } catch {}
    })();
  }, []);

  if (!stats) return <div className="text-sm text-neutral-500">Memuat dashboard…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Dashboard</h1>
        <p className="text-sm text-neutral-500 mt-1">Ringkasan operasional Grippel Rental hari ini.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard testId="stat-bookings-today" icon={CalendarClock} label="Booking Hari Ini" value={stats.bookings_today} onClick={() => nav("/bookings")} />
        <StatCard testId="stat-upcoming" icon={Clock} label="Booking Mendatang" value={stats.upcoming} onClick={() => nav("/bookings")} />
        <StatCard testId="stat-rented" icon={PackageOpen} label="Sedang Disewa" value={stats.rented} onClick={() => nav("/bookings?status=checked_out")} />
        <StatCard testId="stat-due-today" icon={Undo2} label="Kembali Hari Ini" value={stats.due_today} onClick={() => nav("/return")} />
        <StatCard testId="stat-overdue" icon={AlertTriangle} label="Terlambat" value={stats.overdue} accent="gray" onClick={() => nav("/return")} />
        <StatCard testId="stat-utilization" icon={TrendingUp} label="Utilisasi (%)" value={stats.utilization + "%"} onClick={() => nav("/reports")} />
        <StatCard testId="stat-pending-qtn" icon={FileClock} label="Quotation Pending" value={stats.pending_quotations} onClick={() => nav("/quotations")} />
        <StatCard testId="stat-unpaid-inv" icon={ReceiptText} label="Invoice Belum Lunas" value={stats.unpaid_invoices} onClick={() => nav("/invoices")} />
        <StatCard testId="stat-in-maintenance" icon={Wrench} label="Dalam Perawatan" value={stats.in_maintenance} onClick={() => nav("/maintenance")} />
        <StatCard testId="stat-low-stock" icon={AlertTriangle} label="Stok Rendah" value={stats.low_stock?.length || 0} accent="gray" onClick={() => nav("/inventory")} />
        <div className="card-flat p-4 col-span-2 md:col-span-2 lg:col-span-2">
          <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider">
            <Wallet className="h-4 w-4 text-green-600" /> Pendapatan Bulan Ini
          </div>
          <div className="text-3xl font-bold mt-2 tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            {formatIDR(stats.monthly_revenue)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-flat p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Pendapatan 6 Bulan Terakhir</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={charts?.revenue || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000000).toFixed(0)}jt`} />
                <Tooltip formatter={(v) => formatIDR(v)} />
                <Line type="monotone" dataKey="value" stroke="#16a34a" strokeWidth={2} dot={{ r: 3, fill: "#16a34a" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-4">
          <div className="text-sm font-semibold mb-3">Distribusi Status Booking</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={charts?.by_status || []} dataKey="count" nameKey="status" innerRadius={40} outerRadius={80}>
                  {(charts?.by_status || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-4 lg:col-span-2">
          <div className="text-sm font-semibold mb-3">Booking per Bulan</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts?.bookings_by_month || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#16a34a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-4">
          <div className="text-sm font-semibold mb-3">Equipment Paling Sering Disewa</div>
          <div className="space-y-2">
            {(charts?.most_rented || []).map((it, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <div className="truncate">{it.name}</div>
                <div className="font-mono text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">{it.value}x</div>
              </div>
            ))}
            {(!charts?.most_rented || charts.most_rented.length === 0) && (
              <div className="text-xs text-neutral-500">Belum ada data.</div>
            )}
          </div>
        </div>
      </div>

      {stats.low_stock?.length > 0 && (
        <div className="card-flat p-4">
          <div className="flex items-center gap-2 text-sm font-semibold mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Peringatan Stok Rendah
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {stats.low_stock.map((ls) => (
              <div key={ls.id} className="text-sm flex justify-between p-2 bg-amber-50 rounded border border-amber-200">
                <span>{ls.name}</span>
                <span className="text-amber-700 font-medium">{ls.available} / min {ls.min}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
